import * as React from 'react';
import { DatePicker } from '@blueprintjs/datetime';
import styled from '@emotion/styled';
import { css, keyframes } from 'emotion';
import moment, { Moment } from 'moment';
import { IconNames } from '@blueprintjs/icons';
import $ from 'jquery';
import { Button, Tooltip, Intent } from '@blueprintjs/core';
import {
  blackpoint,
  blackpointEconomic,
  blackpointHealthy,
  blackpointSpecial,
  dutyDay,
  dutyDayNight,
  dutyNight,
  dutyOff,
} from '../../assets';
import Toolbox from './Toolbox';
import { Instance } from 'mobx-state-tree';
import { Pharmacy, SystemStatus } from '../../store/models';
import NotificationToast from './NotificationToast';
import withTranslate, { WithTranslateProps } from './WithTranslate';
import { TLCode } from '../../common/enums';
import MomentLocaleUtils from 'react-day-picker/moment';
import { Position } from '@blueprintjs/core/lib/esm/common/position';
import Select from 'react-select';
import { ModalStore } from '../../store';
import { CalendarContextMenu } from './CalendarContextMenu';
import { ContextMenu2, ContextMenu2ContentProps } from '@blueprintjs/popover2';
import { simulateClickOn } from '../../common/clickSimulation';
import { getSemester } from '../../common/utils';

export const dateFormatMultiCalendar = 'YYYY-MM-DD';

export enum MultiCalendarSelectionType {
  dutyOffRequest = 'dutyoffrequest',
  blackpoint = 'blackpoint',
  blackpointHealthy = 'blackpointHealthy',
  blackpointEconomic = 'blackpointEconomic',
  blackpointSpecial = 'blackpointSpecial',
  delete = 'delete',
  dayDuty = 'dayDuty',
  dayNightDuty = 'dayNightDuty',
  inactive = 'inactive',
}

export interface MultiCalendarProps extends WithTranslateProps {
  bankHolidayDates: string[];
  blackPoints: string[];
  blackPointsEconomic: string[];
  blackPointsHealth: string[];
  blackPointsSpecial: string[];
  dayDuties: string[];
  dayNightDuties: string[];
  nightDuties: string[];
  newBlackPoints: string[];
  newBlackPointsEconomic: string[];
  newBlackPointsHealth: string[];
  newBlackPointsSpecial: string[];
  newDayDuties: string[];
  newDayNightDuties: string[];
  deletedDutyOffRequests: string[];
  deletedBlackPoints: string[];
  deletedBlackPointsEconomic: string[];
  deletedBlackPointsHealth: string[];
  deletedBlackPointsSpecial: string[];
  deletedDayDuties: string[];
  deletedDayNightDuties: string[];
  deletedNightDuties: string[];
  deletedExemptions: string[];
  dutyOffRequestDates: string[];
  navigateMonthCount: number;
  onDatesChange: (data: any) => void;
  systemStatus: Array<Instance<typeof SystemStatus>>;
  blackPointHolidayMax?: number;
  blackPointEconomicMax?: number;
  annualFreeBankHolidayMax?: number;
  annualFreeWeekendMax?: number;
  isSecretariat?: boolean;
  fly?: number;
  sly?: number;
  onCalendarMove: (fly: number, sly: number) => void;
  rowCount?: number;
  hideLegend?: boolean;
  synchronizedScrolling?: boolean;
  pharmacyIdForDebugging?: number;
  onTypeChange?: (type: MultiCalendarSelectionType | undefined) => void;
  selectedType?: MultiCalendarSelectionType | undefined;
  separateToolbox?: boolean;
  pharmacyDetails?: Instance<typeof Pharmacy>;
  pharmacyId?: number;
  pharmacyList?: any;
  onPharmacyChange?: (selectedOption: any) => void;
  ModalStore?: Instance<typeof ModalStore>;
}

export interface MultiCalendarState {
  isOpen: boolean;
  selectedDate?: string;
  firstMonthFirstLine: number;
  firstMonthSecondLine: number;
  yearFirstLine: number;
  yearSecondLine: number;
  selectedType?: MultiCalendarSelectionType;
  shiftPressed: boolean;
  isContextualMenuDisabled: boolean;
  nextActionAfterContextMenu: (() => void) | undefined;
}

class MultiCalendar extends React.Component<MultiCalendarProps, MultiCalendarState> {
  state = {
    firstMonthFirstLine: 0,
    firstMonthSecondLine: 6,
    isOpen: false,
    shiftPressed: false,
    yearFirstLine: this.props.fly ? this.props.fly : moment().year(),
    yearSecondLine: this.props.sly ? this.props.sly : moment().year(),
    selectedType: this.props.selectedType ? this.props.selectedType : undefined,
    selectedDate: undefined,
    isContextualMenuDisabled: false,
    nextActionAfterContextMenu: undefined,
  } as MultiCalendarState;

  suggestInput;

  datePickerRef: DatePicker;

  componentDidUpdate(prevProps: MultiCalendarProps) {
    if (prevProps.fly !== this.props.fly) {
      this.setState({ yearFirstLine: Number(this.props.fly) });
    }
    if (prevProps.selectedType !== this.props.selectedType) {
      this.setState({ selectedType: this.props.selectedType, selectedDate: undefined });
    }

    if (this.state.nextActionAfterContextMenu !== undefined) {
      const action = this.state.nextActionAfterContextMenu;
      this.setState({ nextActionAfterContextMenu: undefined });
      action();
    }

    if (this.state.isContextualMenuDisabled) {
      this.setState({ isContextualMenuDisabled: false });
    }

    if (this.suggestInput) this.suggestInput.focus();
  }

  async componentDidMount() {
    if (this.suggestInput) this.suggestInput.focus();
  }

  componentWillUnmount() {
    this.setState({ isContextualMenuDisabled: true });
  }

  handleKeyPress = (e) => {
    if (e.key === 'Shift' && !this.state.shiftPressed) {
      this.setState({ shiftPressed: true });
    }
  };

  handleKeyUnPress = (e) => {
    if (e.key === 'Shift' && this.state.shiftPressed) {
      this.setState({ shiftPressed: false });
    }
  };

  onTypeChange = (type: MultiCalendarSelectionType) => {
    if (this.state.selectedType !== type) {
      this.setState({ selectedType: type, selectedDate: undefined });
      if (this.props.onTypeChange) this.props.onTypeChange(type);
    }
  };

  checkExistingDate = (date: string) => {
    const {
      blackPoints,
      blackPointsHealth,
      blackPointsEconomic,
      blackPointsSpecial,
      dayNightDuties,
      dayDuties,
      nightDuties,
      dutyOffRequestDates,
      newBlackPoints,
      newBlackPointsEconomic,
      newBlackPointsHealth,
      newBlackPointsSpecial,
      newDayDuties,
      newDayNightDuties,
      deletedDutyOffRequests,
      deletedBlackPoints,
      deletedBlackPointsHealth,
      deletedBlackPointsEconomic,
      deletedBlackPointsSpecial,
      deletedDayDuties,
      deletedDayNightDuties,
      deletedNightDuties,
    } = this.props;
    const arrays = [
      newBlackPoints,
      newBlackPointsEconomic,
      newBlackPointsHealth,
      newBlackPointsSpecial,
      newDayDuties,
      newDayNightDuties,
    ];
    let found = false;
    arrays.forEach((array) => {
      if (array.includes(date)) {
        found = true;
      }
    });
    if (dutyOffRequestDates.includes(date) && !deletedDutyOffRequests.includes(date)) found = true;
    if (blackPoints.includes(date) && !deletedBlackPoints.includes(date)) found = true;
    if (blackPointsEconomic.includes(date) && !deletedBlackPointsEconomic.includes(date)) found = true;
    if (blackPointsHealth.includes(date) && !deletedBlackPointsHealth.includes(date)) found = true;
    if (blackPointsSpecial.includes(date) && !deletedBlackPointsSpecial.includes(date)) found = true;
    if (dayDuties.includes(date) && !deletedDayDuties.includes(date)) found = true;
    if (dayNightDuties.includes(date) && !deletedDayNightDuties.includes(date)) found = true;
    if (nightDuties.includes(date) && !deletedNightDuties.includes(date)) found = true;

    return found;
  };

  hasDutyNextYear = () => {
    const dateMin = new Date(this.state.yearSecondLine + 1, 0, 1);
    const dateMax = new Date(this.state.yearSecondLine + 1, 0, 7);

    const dayNightDutyDates = this.props.dayNightDuties.map((dt) => new Date(dt));
    const res2 = dayNightDutyDates.filter((dt) => {
      return dt >= dateMin && dt <= dateMax;
    });
    if (res2 != null && res2.length > 0) return true;

    const dayDutyDates = this.props.dayDuties.map((dt) => new Date(dt));
    const res1 = dayDutyDates.filter((dt) => {
      return dt >= dateMin && dt <= dateMax;
    });
    if (res1 != null && res1.length > 0) return true;

    return false;
  };

  deleteDate = (date: string) => {
    const {
      dutyOffRequestDates,
      blackPoints,
      blackPointsHealth,
      blackPointsEconomic,
      blackPointsSpecial,
      dayDuties,
      dayNightDuties,
      nightDuties,
      deletedDutyOffRequests,
      deletedBlackPoints,
      deletedBlackPointsEconomic,
      deletedBlackPointsHealth,
      deletedBlackPointsSpecial,
      deletedDayDuties,
      deletedNightDuties,
      deletedDayNightDuties,
    } = this.props;
    let {
      newBlackPoints,
      newBlackPointsEconomic,
      newBlackPointsHealth,
      newBlackPointsSpecial,
      newDayDuties,
      newDayNightDuties,
    } = this.props;
    if (newBlackPoints.includes(date)) {
      newBlackPoints = newBlackPoints.filter((e) => e !== date);
    }
    if (newBlackPointsEconomic.includes(date)) {
      newBlackPointsEconomic = newBlackPointsEconomic.filter((e) => e !== date);
    }
    if (newBlackPointsHealth.includes(date)) {
      newBlackPointsHealth = newBlackPointsHealth.filter((e) => e !== date);
    }
    if (newBlackPointsSpecial.includes(date)) {
      newBlackPointsSpecial = newBlackPointsSpecial.filter((e) => e !== date);
    }
    if (newDayDuties.includes(date)) {
      newDayDuties = newDayDuties.filter((e) => e !== date);
    }
    if (newDayNightDuties.includes(date)) {
      newDayNightDuties = newDayNightDuties.filter((e) => e !== date);
    }
    if (dutyOffRequestDates.includes(date)) {
      deletedDutyOffRequests.push(date);
    }
    if (blackPoints.includes(date)) {
      deletedBlackPoints.push(date);
    }
    if (blackPointsHealth.includes(date)) {
      deletedBlackPointsHealth.push(date);
    }
    if (blackPointsEconomic.includes(date)) {
      deletedBlackPointsEconomic.push(date);
    }
    if (blackPointsSpecial.includes(date)) {
      deletedBlackPointsSpecial.push(date);
    }
    if (dayDuties.includes(date)) {
      deletedDayDuties.push(date);
    }
    if (dayNightDuties.includes(date)) {
      deletedDayNightDuties.push(date);
      // deletedNightDuties.push(date);
      // deletedDayDuties.push(date);
    }
    if (nightDuties.includes(date)) {
      deletedNightDuties.push(date);
    }

    this.props.onDatesChange({
      newBlackPoints,
      newBlackPointsEconomic,
      newBlackPointsHealth,
      newBlackPointsSpecial,
      newDayDuties,
      newDayNightDuties,
      deletedDutyOffRequests,
      deletedBlackPoints,
      deletedBlackPointsEconomic,
      deletedBlackPointsHealth,
      deletedBlackPointsSpecial,
      deletedDayDuties,
      deletedDayNightDuties,
      deletedNightDuties,
    });
  };

  findEncodingStatusForYear = (year: number) => {
    return this.props.systemStatus.find((elem) => elem.year === year && elem.subSystem === 'Blackpoint');
  };

  findNextAcceptableBlackpoint = () => {
    const date = this.props.systemStatus.find((elem) => elem.subSystem === 'NextAcceptableBlackpoint');
    if (date) {
      return moment(date.status, 'DD/MM/YYYY');
    } else {
      return moment('01/01/2000', 'DD/MM/YYYY');
    }
  };

  isAcceptableBlackpointDate = (date: Date, first: Moment) => {
    const res = !moment(date).isBefore(first);
    return res;
  };

  getDutyOffRequestsForYearShowed = (arrayNewDutyOffRequest) => {
    const { firstMonthFirstLine, firstMonthSecondLine, yearFirstLine, yearSecondLine } = this.state;
    const { dutyOffRequestDates, deletedDutyOffRequests } = this.props;
    const dateBegin = moment(new Date(yearFirstLine, firstMonthFirstLine, 1));
    const dateEnd = moment(
      new Date(yearSecondLine, firstMonthSecondLine === 6 ? 11 : 5, firstMonthSecondLine === 6 ? 31 : 30),
    ).endOf('day');
    const dutyOff = dutyOffRequestDates
      .filter((elem) => dateBegin.isBefore(moment(elem)) && dateEnd.isAfter(moment(elem)))
      .concat(
        arrayNewDutyOffRequest.filter((elem) => dateBegin.isBefore(moment(elem)) && dateEnd.isAfter(moment(elem))),
      )
      .concat(
        deletedDutyOffRequests.filter((elem) => dateBegin.isBefore(moment(elem)) && dateEnd.isAfter(moment(elem))),
      );

    return dutyOff;
  };

  getBlackPointForYearShowed = (arrayNewBlackPoint) => {
    const { firstMonthFirstLine, firstMonthSecondLine, yearFirstLine, yearSecondLine } = this.state;
    const { blackPoints, deletedBlackPoints } = this.props;
    const dateBegin = moment(new Date(yearFirstLine, firstMonthFirstLine, 1));
    const dateEnd = moment(
      new Date(yearSecondLine, firstMonthSecondLine === 6 ? 11 : 5, firstMonthSecondLine === 6 ? 31 : 30),
    ).endOf('day');
    const blackPointHoliday = blackPoints
      .filter((elem) => dateBegin.isBefore(moment(elem)) && dateEnd.isAfter(moment(elem)))
      .concat(arrayNewBlackPoint.filter((elem) => dateBegin.isBefore(moment(elem)) && dateEnd.isAfter(moment(elem))))
      .concat(deletedBlackPoints.filter((elem) => dateBegin.isBefore(moment(elem)) && dateEnd.isAfter(moment(elem))));

    return blackPointHoliday;
  };

  getEconomicBlackPointForYearShowed = (arrayNewBlackPointEconomic) => {
    const { firstMonthFirstLine, firstMonthSecondLine, yearFirstLine, yearSecondLine } = this.state;
    const { blackPointsEconomic, deletedBlackPointsEconomic } = this.props;
    const dateBegin = moment(new Date(yearFirstLine, firstMonthFirstLine, 1));
    const dateEnd = moment(
      new Date(yearSecondLine, firstMonthSecondLine === 6 ? 11 : 5, firstMonthSecondLine === 6 ? 31 : 30),
    ).endOf('day');
    const blackPointEconomic = blackPointsEconomic
      .filter((elem) => dateBegin.isBefore(moment(elem)) && dateEnd.isAfter(moment(elem)))
      .concat(
        arrayNewBlackPointEconomic.filter((elem) => dateBegin.isBefore(moment(elem)) && dateEnd.isAfter(moment(elem))),
      )
      .concat(
        deletedBlackPointsEconomic.filter((elem) => dateBegin.isBefore(moment(elem)) && dateEnd.isAfter(moment(elem))),
      );

    return blackPointEconomic;
  };

  getBankHolidayForYearShowed = () => {
    const { firstMonthFirstLine, firstMonthSecondLine, yearFirstLine, yearSecondLine } = this.state;
    const { bankHolidayDates } = this.props;
    const dateBegin = moment(new Date(yearFirstLine, firstMonthFirstLine, 1));
    const dateEnd = moment(
      new Date(yearSecondLine, firstMonthSecondLine === 6 ? 11 : 5, firstMonthSecondLine === 6 ? 31 : 30),
    ).endOf('day');
    const bankHolidays = bankHolidayDates.filter(
      (elem) => dateBegin.isBefore(moment(elem)) && dateEnd.isAfter(moment(elem)),
    );

    return bankHolidays;
  };

  getBlackPointHolidayCount = (arrayNewBlackPoint) => {
    return this.getBlackPointForYearShowed(arrayNewBlackPoint).length;
  };

  getBlackPointEconomicCount = (arrayNewEconomicBlackPoint) => {
    return this.getEconomicBlackPointForYearShowed(arrayNewEconomicBlackPoint).length;
  };

  getBlackPointHolidayCountOnBankHoliday = (arrayNewBlackPoint) => {
    const blackPointHoliday = this.getBlackPointForYearShowed(arrayNewBlackPoint);
    const bankHolidays = this.getBankHolidayForYearShowed();
    return blackPointHoliday.filter((elem) => bankHolidays.includes(elem)).length;
  };

  getBlackPointHolidayCountOnWeekends = (arrayNewBlackPoint) => {
    const blackPointHolidays = this.getBlackPointForYearShowed(arrayNewBlackPoint);
    let cnt = 0;
    blackPointHolidays.forEach((holiday) => {
      if (this.IsWeekend(holiday) && !this.IsBankHoliday(holiday)) {
        cnt += 1;
      }
    });
    return cnt;
  };

  IsWeekend = (date) => {
    const day = moment(date)
      .locale('fr')
      .format('dddd');
    return day === 'samedi' || day === 'dimanche';
  };

  IsBankHoliday = (date) => {
    const bankHolidays = this.getBankHolidayForYearShowed();
    return bankHolidays.includes(date);
  };

  checkCanEdit = (date: Date, arrayNewBlackPoint, arrayNewEconomicBlackPoint) => {
    if (date == null) return false;
    const year = date.getFullYear();
    // const status = this.findEncodingStatusForYear(year);
    const first = this.findNextAcceptableBlackpoint();
    const { blackPointHolidayMax, blackPointEconomicMax, annualFreeBankHolidayMax, annualFreeWeekendMax } = this.props;

    let canEdit = true;
    // if (status == null || (!this.isAcceptableBlackpointDate(date, first) && !this.props.isSecretariat!)) {
    if (!this.isAcceptableBlackpointDate(date, first) && !this.props.isSecretariat!) {
      // (status.status !== 'EncodingOpen' && !this.props.isSecretariat!)) {
      canEdit = false;
      NotificationToast.showError(this.props.translate(TLCode.MULTI_CALENDAR_ErrorMsgEncodingNotOpenForThisDate));
    } else if (
      this.state.selectedType === MultiCalendarSelectionType.blackpoint &&
      blackPointHolidayMax != null &&
      annualFreeBankHolidayMax != null &&
      annualFreeWeekendMax != null
    ) {
      const countBlackPointHoliday = this.getBlackPointHolidayCount(arrayNewBlackPoint);
      if (countBlackPointHoliday >= blackPointHolidayMax) {
        canEdit = false;
        NotificationToast.showError(this.props.translate(TLCode.MULTI_CALENDAR_ErrorMsgTooManyBlackPoints));
      }
      const countBlackPointHolidayOnBankHoliday = this.getBlackPointHolidayCountOnBankHoliday(arrayNewBlackPoint);
      if (
        this.props.bankHolidayDates.includes(moment(date).format(dateFormatMultiCalendar)) &&
        countBlackPointHolidayOnBankHoliday >= annualFreeBankHolidayMax
      ) {
        canEdit = false;
        NotificationToast.showError(
          this.props.translate(TLCode.MULTI_CALENDAR_ErrorMsgTooManyBlackPointsOnBankHolidays),
        );
      }
      const countBlackPointHolidayOnWeekends = this.getBlackPointHolidayCountOnWeekends(arrayNewBlackPoint);
      if (this.IsWeekend(date) && countBlackPointHolidayOnWeekends >= annualFreeWeekendMax) {
        canEdit = false;
        NotificationToast.showError(this.props.translate(TLCode.MULTI_CALENDAR_ErrorMsgTooManyBlackPointsOnWeekends));
      }
    } else if (
      this.state.selectedType === MultiCalendarSelectionType.blackpointEconomic &&
      blackPointEconomicMax != null
    ) {
      const countBlackPointEconomic = this.getBlackPointEconomicCount(arrayNewEconomicBlackPoint);
      // le secrétariat peut dépasser le quota mais pas la pharmacie
      if (!this.props.isSecretariat && countBlackPointEconomic >= blackPointEconomicMax) {
        canEdit = false;
        NotificationToast.showError(this.props.translate(TLCode.MULTI_CALENDAR_ErrorMsgTooManyEconomicBlackPoints));
      }
    }
    return canEdit;
  };

  onDateSelect = async (date: Date) => {
    const { selectedType, selectedDate, shiftPressed } = this.state;
    const { deletedExemptions, newDayDuties, newDayNightDuties, ModalStore } = this.props;

    const realSelectedType =
      selectedType === MultiCalendarSelectionType.inactive
        ? MultiCalendarSelectionType.blackpointSpecial
        : selectedType;

    const dateStr = moment(date).format(dateFormatMultiCalendar);
    let dateExisting = this.checkExistingDate(dateStr);
    const canEdit = this.checkCanEdit(date, this.props.newBlackPoints, this.props.newBlackPointsEconomic);
    if (realSelectedType != null && canEdit) {
      // PG-  JLL added
      // if (this.props.dutyOffRequestDates.includes(dateStr) &&
      // (
      //   realSelectedType === MultiCalendarSelectionType.blackpoint || realSelectedType === MultiCalendarSelectionType.blackpointEconomic ||  realSelectedType === MultiCalendarSelectionType.blackpointHealthy
      // )) {
      if (this.props.dutyOffRequestDates.includes(dateStr)) {
        if (
          realSelectedType === MultiCalendarSelectionType.dayDuty ||
          realSelectedType === MultiCalendarSelectionType.dayNightDuty
        ) {
          const confirm = await ModalStore!.confirm.show(
            this.props.translate(TLCode.MULTI_CALENDAR_REMOVE_EXEMPTION_TITLE),
            this.props.translate(TLCode.MULTI_CALENDAR_REMOVE_EXEMPTION_CONTENT),
          );
          if (confirm) {
            deletedExemptions.push(dateStr);
            const changedData: any = {
              deletedExemptions,
            };
            if (realSelectedType === MultiCalendarSelectionType.dayDuty) {
              changedData.newDayDuties = newDayDuties;
              changedData.newDayDuties.push(dateStr);
            } else {
              changedData.newDayNightDuties = newDayNightDuties;
              changedData.newDayNightDuties.push(dateStr);
            }
            this.props.onDatesChange(changedData);
          }
        } else {
          NotificationToast.showInfo(this.props.translate(TLCode.MULTI_CALENDAR_InfoMsgAlreadyExempted));
        }
      } else if (realSelectedType === MultiCalendarSelectionType.delete && dateExisting) {
        this.deleteDate(dateStr);
        // PG-449 JLL changed
        // } else if (realSelectedType !== MultiCalendarSelectionType.delete && dateStr !== selectedDate && date != null &&
        //   !dateExisting
        // ) {
        //end
      } else if (realSelectedType !== MultiCalendarSelectionType.delete && dateStr !== selectedDate && date != null) {
        let selectedArray;
        let deletedCorrespondingArray;
        let previousDelete;
        // PG-449 JLL added
        if (dateExisting) {
          this.deleteDate(dateStr);
        }
        // end
        switch (realSelectedType) {
          case MultiCalendarSelectionType.dutyOffRequest:
            // selectedArray = this.props.newDayNightDuties;
            deletedCorrespondingArray = this.props.deletedDutyOffRequests;
            break;
          case MultiCalendarSelectionType.blackpoint:
            selectedArray = this.props.newBlackPoints;
            deletedCorrespondingArray = this.props.deletedBlackPoints;
            break;
          case MultiCalendarSelectionType.blackpointEconomic:
            selectedArray = this.props.newBlackPointsEconomic;
            deletedCorrespondingArray = this.props.deletedBlackPointsEconomic;
            break;
          case MultiCalendarSelectionType.blackpointHealthy:
            selectedArray = this.props.newBlackPointsHealth;
            deletedCorrespondingArray = this.props.deletedBlackPointsHealth;
            break;
          case MultiCalendarSelectionType.blackpointSpecial:
            selectedArray = this.props.newBlackPointsSpecial;
            deletedCorrespondingArray = this.props.deletedBlackPointsSpecial;
            break;
          case MultiCalendarSelectionType.dayDuty:
            selectedArray = newDayDuties;
            deletedCorrespondingArray = this.props.deletedDayDuties;
            break;
          case MultiCalendarSelectionType.dayNightDuty:
            selectedArray = newDayNightDuties;
            deletedCorrespondingArray = this.props.deletedDayNightDuties;
            break;
        }
        if (
          shiftPressed &&
          selectedDate != null &&
          realSelectedType !== MultiCalendarSelectionType.dayDuty &&
          realSelectedType !== MultiCalendarSelectionType.dayNightDuty
        ) {
          selectedArray = selectedArray.filter((date) => {
            return date !== dateStr;
          });
          const dateFirst = moment(dateStr).isBefore(moment(selectedDate))
            ? moment(dateStr).startOf('day')
            : moment(selectedDate).startOf('day');
          const dateEnd = moment(dateStr).isBefore(moment(selectedDate))
            ? moment(selectedDate).endOf('day')
            : moment(dateStr).endOf('day');
          while (dateFirst.isBefore(dateEnd)) {
            const dateFirstStr = dateFirst.format(dateFormatMultiCalendar);
            dateExisting = this.checkExistingDate(dateFirstStr);
            previousDelete = deletedCorrespondingArray.includes(dateFirstStr);
            if (!dateExisting && this.checkCanEdit(dateFirst.toDate(), selectedArray, selectedArray)) {
              if (previousDelete) {
                deletedCorrespondingArray = deletedCorrespondingArray.filter((d) => d !== dateFirstStr);
              } else {
                selectedArray.push(dateFirstStr);
              }
            }
            dateFirst.add(1, 'd');
          }
          this.setState({
            selectedDate: undefined,
          });
        } else {
          previousDelete = deletedCorrespondingArray.includes(dateStr);
          if (previousDelete) {
            deletedCorrespondingArray = deletedCorrespondingArray.filter((d) => d !== dateStr);
          } // else {
          selectedArray.push(dateStr);
          // }
          this.setState({
            selectedDate: dateStr,
          });
        }
        switch (realSelectedType) {
          case MultiCalendarSelectionType.dutyOffRequest:
            this.props.onDatesChange({
              newDutyOffRequests: selectedArray,
              deletedDutyOffRequests: deletedCorrespondingArray,
            });
            break;
          case MultiCalendarSelectionType.blackpoint:
            this.props.onDatesChange({
              newBlackPoints: selectedArray,
              deletedBlackPoints: deletedCorrespondingArray,
            });
            break;
          case MultiCalendarSelectionType.blackpointEconomic:
            this.props.onDatesChange({
              newBlackPointsEconomic: selectedArray,
              deletedBlackPointsEconomic: deletedCorrespondingArray,
            });
            break;
          case MultiCalendarSelectionType.blackpointHealthy:
            this.props.onDatesChange({
              newBlackPointsHealth: selectedArray,
              deletedBlackPointsHealth: deletedCorrespondingArray,
            });
            break;
          case MultiCalendarSelectionType.blackpointSpecial:
            this.props.onDatesChange({
              newBlackPointsSpecial: selectedArray,
              deletedBlackPointsSpecial: deletedCorrespondingArray,
            });
            break;
          case MultiCalendarSelectionType.dayDuty:
            this.props.onDatesChange({
              newDayDuties: selectedArray,
              deletedDayDuties: deletedCorrespondingArray,
            });
            break;
          case MultiCalendarSelectionType.dayNightDuty:
            this.props.onDatesChange({
              newDayNightDuties: selectedArray,
              deletedDayNightDuties: deletedCorrespondingArray,
            });
            break;
        }
      }
    }
  };

  dayDuty = (day) => {
    return (
      this.props.dayDuties.indexOf(moment(day).format(dateFormatMultiCalendar)) > -1 &&
      !this.props.deletedDayDuties.includes(moment(day).format(dateFormatMultiCalendar))
    );
  };

  nightDuty = (day) => {
    return (
      this.props.nightDuties.indexOf(moment(day).format(dateFormatMultiCalendar)) > -1 &&
      !this.props.deletedNightDuties.includes(moment(day).format(dateFormatMultiCalendar))
    );
  };

  dayNightDuty = (day) => {
    return (
      this.props.dayNightDuties.indexOf(moment(day).format(dateFormatMultiCalendar)) > -1 &&
      !this.props.deletedDayNightDuties.includes(moment(day).format(dateFormatMultiCalendar))
    );
  };

  blackPoint = (day) => {
    return (
      this.props.blackPoints.indexOf(moment(day).format(dateFormatMultiCalendar)) > -1 &&
      !this.props.deletedBlackPoints.includes(moment(day).format(dateFormatMultiCalendar))
    );
  };

  blackPointsEconomic = (day) => {
    return (
      this.props.blackPointsEconomic.indexOf(moment(day).format(dateFormatMultiCalendar)) > -1 &&
      !this.props.deletedBlackPointsEconomic.includes(moment(day).format(dateFormatMultiCalendar))
    );
  };

  blackPointsHealth = (day) => {
    return (
      this.props.blackPointsHealth.indexOf(moment(day).format(dateFormatMultiCalendar)) > -1 &&
      !this.props.deletedBlackPointsHealth.includes(moment(day).format(dateFormatMultiCalendar))
    );
  };

  blackPointsSpecial = (day) => {
    return (
      this.props.blackPointsSpecial.indexOf(moment(day).format(dateFormatMultiCalendar)) > -1 &&
      !this.props.deletedBlackPointsSpecial.includes(moment(day).format(dateFormatMultiCalendar))
    );
  };

  highlighted = (day) => {
    return day.toDateString() === new Date().toDateString(); // today
  };

  newBlackPoint = (day) => {
    return this.props.newBlackPoints.indexOf(moment(day).format(dateFormatMultiCalendar)) > -1;
  };

  newBlackPointsEconomic = (day) => {
    return this.props.newBlackPointsEconomic.indexOf(moment(day).format(dateFormatMultiCalendar)) > -1;
  };

  newBlackPointsHealth = (day) => {
    return this.props.newBlackPointsHealth.indexOf(moment(day).format(dateFormatMultiCalendar)) > -1;
  };

  newBlackPointsSpecial = (day) => {
    return this.props.newBlackPointsSpecial.indexOf(moment(day).format(dateFormatMultiCalendar)) > -1;
  };

  newDayDuty = (day) => {
    return this.props.newDayDuties.indexOf(moment(day).format(dateFormatMultiCalendar)) > -1;
  };

  newDayNightDuty = (day) => {
    return this.props.newDayNightDuties.indexOf(moment(day).format(dateFormatMultiCalendar)) > -1;
  };

  bankHolidays = (day) => {
    return this.props.bankHolidayDates.indexOf(moment(day).format(dateFormatMultiCalendar)) > -1;
  };

  dutyOffRequest = (day) => {
    return (
      this.props.dutyOffRequestDates.indexOf(moment(day).format(dateFormatMultiCalendar)) > -1 &&
      !this.props.deletedExemptions.includes(moment(day).format(dateFormatMultiCalendar))
    );
  };

  changed = (day) => {
    const {
      newBlackPoints,
      newBlackPointsEconomic,
      newBlackPointsHealth,
      newBlackPointsSpecial,
      newDayDuties,
      newDayNightDuties,
      deletedDutyOffRequests,
      deletedBlackPoints,
      deletedBlackPointsHealth,
      deletedBlackPointsEconomic,
      deletedBlackPointsSpecial,
      deletedDayDuties,
      deletedNightDuties,
      deletedDayNightDuties,
    } = this.props;
    const arrays = [
      newBlackPoints,
      newBlackPointsEconomic,
      newBlackPointsHealth,
      newBlackPointsSpecial,
      newDayDuties,
      newDayNightDuties,
      deletedDutyOffRequests,
      deletedBlackPoints,
      deletedBlackPointsHealth,
      deletedBlackPointsEconomic,
      deletedBlackPointsSpecial,
      deletedDayDuties,
      deletedNightDuties,
      deletedDayNightDuties,
    ];
    let changed = false;
    arrays.forEach((array) => {
      if (array.indexOf(moment(day).format(dateFormatMultiCalendar)) > -1) {
        changed = true;
      }
    });
    return changed;
  };

  getCountBlackPointOnBankHoliday = () => {
    return this.getBlackPointHolidayCountOnBankHoliday(this.props.newBlackPoints);
  };

  setBlackpointTypeForSemesterFrom = (date: Date) => {
    const semester = getSemester(
      moment(date)
        .add(-6, 'months')
        .toDate(),
    );
    while (date <= semester.end) {
      this.onDateSelect(date);
      date = moment(date)
        .add(1, 'day')
        .toDate();
    }
  };

  monthMap = new Map<String, String>([
    ['janv.', '01'],
    ['févr.', '02'],
    ['mars', '03'],
    ['avr.', '04'],
    ['mai', '05'],
    ['juin', '06'],
    ['juil.', '07'],
    ['août', '08'],
    ['sept.', '09'],
    ['oct.', '10'],
    ['nov.', '11'],
    ['déc.', '12'],
  ]);

  extractDateFromDayPickerDateValue = (date: String) => {
    const dateParts = date.substring(date.indexOf(' ') + 1).split(' ');
    const formattedDate = dateParts[0] + ' ' + this.monthMap.get(dateParts[1]) + ' ' + dateParts[2];
    return moment(formattedDate, 'DD MM YYYY', 'fr')
      .locale('fr')
      .toDate();
  };

  contextMenuToolTypeClicked = (e: Element) => {
    //Note, the date (string) formatted from the datePicker element might change (after an upgrade), using directly a format does not work for all month.
    //Hence the string manipulation to get an easier format to handle.
    if (e.className.startsWith('DayPicker-Day')) {
      const ariaLabel = e.getAttribute('aria-label');
      if (ariaLabel !== null) {
        const date = this.extractDateFromDayPickerDateValue(ariaLabel);
        this.onDateSelect(date);
      }
    }
  };

  contextMenuInactiveClicked = (e: Element) => {
    if (e.className.startsWith('DayPicker-Day')) {
      const ariaLabel = e.getAttribute('aria-label');
      if (ariaLabel !== null) {
        var date = this.extractDateFromDayPickerDateValue(ariaLabel);
        this.setBlackpointTypeForSemesterFrom(date);
      }
    }
  };

  MultiCalendarDatePicker = (key: string, initialMonth: Date, minDate: Date, maxDate: Date): JSX.Element => {
    return (
      <ContextMenu2
        key={key}
        content={(contextMenu2ContentProps: ContextMenu2ContentProps) =>
          CalendarContextMenu({
            ...contextMenu2ContentProps,
            blackPointHolidayCount: this.getCountBlackPointOnBankHoliday(),
            blackPointHolidayMax: this.props.blackPointHolidayMax,
            isSecretariat: this.props.isSecretariat,
            onTypeClicked: (type: MultiCalendarSelectionType) => {
              this.setState({ isContextualMenuDisabled: true });
              this.setState({
                nextActionAfterContextMenu: () => {
                  simulateClickOn(
                    contextMenu2ContentProps.targetOffset!.left,
                    contextMenu2ContentProps.targetOffset!.top,
                    type === MultiCalendarSelectionType.inactive
                      ? this.contextMenuInactiveClicked
                      : this.contextMenuToolTypeClicked,
                  );
                },
              });
              this.onTypeChange(type);
            },
            selectedType: this.state.selectedType,
            translate: this.props.translate,
          })
        }
        disabled={this.state.isContextualMenuDisabled}
      >
        <DatePicker
          key={key}
          initialMonth={initialMonth}
          minDate={minDate}
          maxDate={maxDate}
          ref={(dp) => {
            if (dp !== null) this.datePickerRef = dp;
          }}
          modifiers={{
            bankHolidays: this.bankHolidays,
            dayDuty: this.dayDuty,
            nightDuty: this.nightDuty,
            dayNightDuty: this.dayNightDuty,
            blackPoint: this.blackPoint,
            blackPointsEconomic: this.blackPointsEconomic,
            blackPointsHealth: this.blackPointsHealth,
            blackPointsSpecial: this.blackPointsSpecial,
            dutyOffRequest: this.dutyOffRequest,
            highlighted: this.highlighted,
            newBlackPoint: this.newBlackPoint,
            newBlackPointsEconomic: this.newBlackPointsEconomic,
            newBlackPointsHealth: this.newBlackPointsHealth,
            newBlackPointsSpecial: this.newBlackPointsSpecial,
            newDayDuty: this.newDayDuty,
            newDayNightDuty: this.newDayNightDuty,
            changed: this.changed,
            weekends: this.IsWeekend,
          }}
          onChange={(date: Date) => {
            if (this.state.selectedType !== MultiCalendarSelectionType.inactive) this.onDateSelect(date);
            else this.setBlackpointTypeForSemesterFrom(date);
          }}
          value={undefined}
          dayPickerProps={{
            firstDayOfWeek: 1,
            localeUtils: MomentLocaleUtils,
            locale: 'fr',
            showOutsideDays: false,
          }}
        />
      </ContextMenu2>
    );
  };

  getMinMaxDatesSixMonths = (year, initialMonth) => {
    const minDate = new Date(year, initialMonth, 1);
    let maxDate;
    for (let i = initialMonth; i < initialMonth + 6; i++) {
      maxDate = i === 11 ? new Date(year + 1, 0) : new Date(year, i + 1);
      maxDate.setDate(maxDate.getDate() - 1);
    }

    const minDt = moment(minDate, 'YYYY-MM-DD');
    const maxDt = moment(maxDate, 'YYYY-MM-DD');

    return { minDt, maxDt };
  };

  getMinMaxDatesTwelveMonths = (year, initialMonth) => {
    const minDate = new Date(year, initialMonth, 1);
    let maxDate;
    for (let i = initialMonth; i < initialMonth + 13; i++) {
      maxDate = i >= 11 ? new Date(year + 1, i % 11) : new Date(year, i + 1);
      maxDate.setDate(maxDate.getDate() - 1);
    }

    const minDt = moment(minDate, 'YYYY-MM-DD');
    const maxDt = moment(maxDate, 'YYYY-MM-DD');

    return { minDt, maxDt };
  };

  getNumberOfDuties = (duties: string[], minDt: Moment, maxDt: Moment) => {
    if (duties.length === 0) return 0;
    return duties.filter((elem) => {
      return moment(elem, 'YYYY-MM-DD').isSameOrAfter(minDt) && moment(elem, 'YYYY-MM-DD').isSameOrBefore(maxDt);
    })!.length;
  };

  getDutiesCountFrom = (duties: string[]) => {
    const { firstMonthFirstLine, yearFirstLine } = this.state;

    const { minDt, maxDt } = this.getMinMaxDatesTwelveMonths(yearFirstLine, firstMonthFirstLine);

    return this.getNumberOfDuties(duties, minDt, maxDt);
  };

  getDayDutiesCount = (): number => this.getDutiesCountFrom(this.props.dayDuties);

  getDayNightDutiesCount = (): number => this.getDutiesCountFrom(this.props.dayNightDuties);

  getNightDutiesCount = (): number => this.getDutiesCountFrom(this.props.nightDuties);

  drawSixMonths = (year, initialMonth) => {
    const months: JSX.Element[] = [];
    let maxDate;
    for (let i = initialMonth; i < initialMonth + 6; i++) {
      maxDate = i === 11 ? new Date(year + 1, 0) : new Date(year, i + 1);
      maxDate.setDate(maxDate.getDate() - 1);
      months.push(this.MultiCalendarDatePicker(year + '' + i, new Date(year, i), new Date(year, i), maxDate));
    }

    return months;
  };

  drawTwelveMonths = (year, initialMonth) => {
    const months: JSX.Element[] = [];
    let maxDate;
    const randomkey = this.randomize();
    for (let i = initialMonth; i < initialMonth + 13; i++) {
      maxDate = i >= 11 ? new Date(year + 1, i % 11) : new Date(year, i + 1);
      maxDate.setDate(maxDate.getDate() - 1);
      months.push(
        <span key={'base_' + randomkey + '_' + year + '' + i}>
          {this.MultiCalendarDatePicker(randomkey + '_' + year + '' + i, new Date(year, i), new Date(year, i), maxDate)}
        </span>,
      );
    }
    return months;
  };

  randomize = () => {
    return '' + Math.random();
  };

  onScroll = (e) => {
    if (this.props.synchronizedScrolling) {
      $('.scrollingCalendar').each(function(index, element: any) {
        if (element !== e.target) {
          const $element: any = $(element);
          const $target: any = $(e.target);
          $element.scrollTop($target.scrollTop());
          $element.scrollLeft($target.scrollLeft());
        }
      });
    }
  };

  navigate = (direction: 'UP' | 'DOWN') => {
    let { firstMonthFirstLine, firstMonthSecondLine, yearFirstLine, yearSecondLine } = this.state;
    const { navigateMonthCount } = this.props;

    if (direction === 'DOWN') {
      firstMonthFirstLine += navigateMonthCount;
      firstMonthSecondLine += navigateMonthCount;
      if (firstMonthFirstLine > 11) {
        firstMonthFirstLine -= 12;
        yearFirstLine++;
      }
      if (firstMonthSecondLine > 11) {
        firstMonthSecondLine -= 12;
        yearSecondLine++;
      }
    } else if (direction === 'UP') {
      firstMonthFirstLine -= navigateMonthCount;
      firstMonthSecondLine -= navigateMonthCount;
      if (firstMonthFirstLine < 0) {
        firstMonthFirstLine += 12;
        yearFirstLine--;
      }
      if (firstMonthSecondLine < 0) {
        firstMonthSecondLine += 12;
        yearSecondLine--;
      }
    }

    this.setState({ firstMonthFirstLine, firstMonthSecondLine, yearFirstLine, yearSecondLine });
    this.props.onCalendarMove(yearFirstLine, yearSecondLine);
  };

  render() {
    const { firstMonthFirstLine, firstMonthSecondLine, yearFirstLine, yearSecondLine } = this.state;
    const { systemStatus, translate } = this.props;
    const { isSecretariat } = this.props;

    const countBlackPointHoliday = this.getBlackPointHolidayCount(this.props.newBlackPoints);
    const countBlackPointEconomic = this.getBlackPointEconomicCount(this.props.newBlackPointsEconomic);
    const countBlackPointOnBankHoliday = this.getBlackPointHolidayCountOnBankHoliday(this.props.newBlackPoints);
    const countBlackPointOnWeekend = this.getBlackPointHolidayCountOnWeekends(this.props.newBlackPoints);
    const encodingStatus = this.findEncodingStatusForYear(yearFirstLine);
    const encodingStatusStr = encodingStatus ? encodingStatus.status : null;

    const firstDate = this.props.systemStatus
      ? this.props.systemStatus.find((elem) => elem.subSystem === 'NextAcceptableBlackpoint')
        ? moment(
            this.props.systemStatus.find((elem) => elem.subSystem === 'NextAcceptableBlackpoint')!.status,
            'DD/MM/YYYY',
          ).format('DD/MM/YYYY')
        : ''
      : '';

    return (
      <span>
        {this.props.separateToolbox && (
          <Toolbox
            blackPointHolidayCount={countBlackPointHoliday}
            blackPointEconomicCount={countBlackPointEconomic}
            blackPointEncodingStatus={encodingStatusStr}
            blackPointHolidayMax={this.props.blackPointHolidayMax}
            blackPointEconomicMax={this.props.blackPointEconomicMax}
            annualFreeBankHolidayMax={this.props.annualFreeBankHolidayMax}
            blackPointOnBankHolidayCount={countBlackPointOnBankHoliday}
            annualFreeWeekendMax={this.props.annualFreeWeekendMax}
            blackPointOnWeekendCount={countBlackPointOnWeekend}
            isSecretariat={isSecretariat}
            navigate={this.navigate}
            fly={yearFirstLine}
            systemStatus={systemStatus}
            firstDate={firstDate}
            hideLegend={false}
            hideCounters={true}
            onTypeChange={this.onTypeChange}
            selectedType={this.state.selectedType}
            dayDutiesCount={this.getDayDutiesCount()}
            dayNightDutiesCount={this.getDayNightDutiesCount()}
            nightDutiesCount={this.getNightDutiesCount()}
          />
        )}
        <HorizontalContainer>
          {this.props.pharmacyList && (
            // <FullWidthRowReverse>
            <div style={{ marginRight: '20px' }}>
              <Select
                className={Searchbox.toString()}
                ref={(input) => {
                  this.suggestInput = input;
                }}
                autoFocus
                value={this.props.pharmacyId}
                onChange={this.props.onPharmacyChange}
                options={this.props.pharmacyList.map((elem) => {
                  return {
                    value: elem.pharmacyId,
                    label:
                      '(' +
                      elem.level +
                      ') - ' +
                      elem.group +
                      ' - ' +
                      elem.apbNumber +
                      ' ' +
                      elem.name +
                      ' @ ' +
                      elem.city,
                  };
                })}
              />
            </div>
          )
          // </FullWidthRowReverse>
          }
          {this.props.pharmacyDetails && (
            <span>
              <b>
                ({this.props.pharmacyDetails!.level})&nbsp;-&nbsp;({this.props.pharmacyDetails!.group}
                )&nbsp;-&nbsp;&nbsp;{this.props.pharmacyDetails!.apbNumber}&nbsp;-&nbsp;
                {this.props.pharmacyDetails!.name}
                &nbsp;-&nbsp;{this.props.pharmacyDetails!.address},&nbsp;
                {this.props.pharmacyDetails!.zipCode}&nbsp;
                {this.props.pharmacyDetails!.city}
              </b>
              {/* <br /> */}
            </span>
          )}
        </HorizontalContainer>
        <VerticalContainer onKeyDown={this.handleKeyPress} onKeyUp={this.handleKeyUnPress}>
          <HorizontalContainer>
            <Toolbox
              blackPointHolidayCount={countBlackPointHoliday}
              blackPointEconomicCount={countBlackPointEconomic}
              blackPointEncodingStatus={encodingStatusStr}
              blackPointHolidayMax={this.props.blackPointHolidayMax}
              blackPointEconomicMax={this.props.blackPointEconomicMax}
              annualFreeBankHolidayMax={this.props.annualFreeBankHolidayMax}
              blackPointOnBankHolidayCount={countBlackPointOnBankHoliday}
              annualFreeWeekendMax={this.props.annualFreeWeekendMax}
              blackPointOnWeekendCount={countBlackPointOnWeekend}
              isSecretariat={isSecretariat}
              navigate={this.navigate}
              fly={yearFirstLine}
              systemStatus={systemStatus}
              firstDate={firstDate}
              hideLegend={this.props.hideLegend || this.props.separateToolbox}
              onTypeChange={this.onTypeChange}
              selectedType={this.props.selectedType}
              dayDutiesCount={this.getDayDutiesCount()}
              dayNightDutiesCount={this.getDayNightDutiesCount()}
              nightDutiesCount={this.getNightDutiesCount()}
            />
          </HorizontalContainer>
          <HorizontalContainer>
            <ContainerCalendars>
              {(this.props.rowCount === undefined || this.props.rowCount === 2) && (
                <>
                  <DivLine>{this.drawSixMonths(yearFirstLine, firstMonthFirstLine)}</DivLine>
                  <DivLine>{this.drawSixMonths(yearSecondLine, firstMonthSecondLine)}</DivLine>
                </>
              )}
              {this.props.rowCount === 1 && (
                <DivScrollLine onScroll={this.onScroll} className="scrollingCalendar">
                  {this.drawTwelveMonths(yearFirstLine, firstMonthFirstLine)}
                </DivScrollLine>
              )}
            </ContainerCalendars>
            {!this.props.hideLegend && (
              <DivVerticallyCenter>
                <NavigateContainer>
                  <Tooltip
                    content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_PreviousYear)}
                    position={Position.BOTTOM}
                    hoverOpenDelay={750}
                  >
                    <Button
                      icon={IconNames.CHEVRON_UP}
                      large={true}
                      style={{ height: '30px', marginLeft: '5px' }}
                      onClick={() => this.navigate('UP')}
                    />
                  </Tooltip>
                  <Tooltip
                    content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_NextYear)}
                    position={Position.BOTTOM}
                    hoverOpenDelay={750}
                  >
                    <Button
                      icon={IconNames.CHEVRON_DOWN}
                      large={true}
                      style={{ height: '30px', marginLeft: '5px' }}
                      onClick={() => this.navigate('DOWN')}
                      intent={this.hasDutyNextYear() ? Intent.DANGER : Intent.NONE}
                      className={this.hasDutyNextYear() ? flashingButton : ''}
                    />
                  </Tooltip>
                </NavigateContainer>
              </DivVerticallyCenter>
            )}
          </HorizontalContainer>
        </VerticalContainer>
      </span>
    );
  }
}

export default withTranslate(MultiCalendar);

const flashing = keyframes`
  0% {opacity:1.0;}
  25% {opacity:0.6;}
  50% {opacity:0.4;}
  75% {opacity:0.6;}
  100% {opacity:1.0;}
`;

const flashingButton = css({
  animationName: flashing,
  animationDuration: '1s',
  animationTimingFunction: 'linear',
  animationIterationCount: 'infinite',
});

const Searchbox = css({
  width: '300px',
});

const VerticalContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const HorizontalContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
`;

const ContainerCalendars = styled.div`
  display: flex;
  flex-direction: column;
  & .DayPicker-Day.DayPicker-Day--selected {
    background-color: white;
    color: black;
  }
  & .DayPicker-Day.DayPicker-Day--dayDuty {
    background-image: url(${dutyDay});
    color: black;
  }
  & .DayPicker-Day.DayPicker-Day--nightDuty {
    background-image: url(${dutyNight});
    color: black;
  }
  & .DayPicker-Day.DayPicker-Day--dayNightDuty {
    background-image: url(${dutyDayNight});
    color: black;
  }
  & .DayPicker-Day.DayPicker-Day--blackPoint {
    background-image: url(${blackpoint});
    color: white;
  }
  & .DayPicker-Day.DayPicker-Day--blackPointsHealth {
    background-image: url(${blackpointHealthy});
    color: black;
  }
  & .DayPicker-Day.DayPicker-Day--blackPointsSpecial {
    background-image: url(${blackpointSpecial});
    color: black;
  }
  & .DayPicker-Day.DayPicker-Day--blackPointsEconomic {
    background-image: url(${blackpointEconomic});
    color: black;
  }
  & .DayPicker-Day.DayPicker-Day--newBlackPoint {
    background-image: url(${blackpoint});
    color: white;
  }
  & .DayPicker-Day.DayPicker-Day--newBlackPointsHealth {
    background-image: url(${blackpointHealthy});
    color: black;
  }
  & .DayPicker-Day.DayPicker-Day--newBlackPointsSpecial {
    background-image: url(${blackpointSpecial});
    color: black;
  }
  & .DayPicker-Day.DayPicker-Day--newBlackPointsEconomic {
    background-image: url(${blackpointEconomic});
    color: black;
  }
  & .DayPicker-Day.DayPicker-Day--newDayDuty {
    background-image: url(${dutyDay});
    color: black;
  }
  & .DayPicker-Day.DayPicker-Day--newDayNightDuty {
    background-image: url(${dutyDayNight});
    color: black;
  }
  & .DayPicker-Day.DayPicker-Day--dutyOffRequest {
    background-image: url(${dutyOff});
  }
  & .DayPicker-Day.DayPicker-Day--bankHolidays {
    color: #b52d4f;
    font-weight: bold;
  }
  & .DayPicker-Day.DayPicker-Day--weekends {
    background-color: #f4f4f4;
  }
  & .DayPicker-Day.DayPicker-Day--changed {
    border-color: red;
    border-width: 2px;
    border-style: solid;
  }
  & .DayPicker-Day.DayPicker-Day--highlighted {
    background-color: #669eff;
    font-weight: bold;
    color: black;
  }
  & .DayPicker-Day.DayPicker-Day--outside {
    background-color: white;
    opacity: 0;
  }
  & .DayPicker-NavButton {
    display: none;
  }
`;

const NavigateContainer = styled.div`
  display: flex;
  flex-direction: column;
  & .bp3-button {
    margin-bottom: 5px;
  }
`;

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
`;

const DivScrollLine = styled.div`
  display: flex;
  flex-direction: row;
  width: 50%;
  overflow: auto;
`;

const DivVerticallyCenter = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;
